import { FC } from 'react';

import Badge from 'molecules/Badge';
import Button, { ButtonProps } from 'molecules/Button';

import { PricingTableConfigProps } from 'components/Tables/PricingV2/config';
import { pricingPlanTitleStyle } from 'components/Tables/PricingV2/styles';

interface TableHeadingProps extends Pick<
  PricingTableConfigProps,
  | 'tableHeading'
  | 'tierOneTitle'
  | 'tierOneLabel'
  | 'tierOneLink'
  | 'tierOneStartIcon'
  | 'tierOneEndIcon'
  | 'tierOneHighlighted'
  | 'tierOneChiliPiperUrl'
  | 'tierTwoTitle'
  | 'tierTwoLabel'
  | 'tierTwoLink'
  | 'tierTwoStartIcon'
  | 'tierTwoEndIcon'
  | 'tierTwoHighlighted'
  | 'tierTwoChiliPiperUrl'
  | 'tierThreeTitle'
  | 'tierThreeLabel'
  | 'tierThreeLink'
  | 'tierThreeStartIcon'
  | 'tierThreeEndIcon'
  | 'tierThreeHighlighted'
  | 'tierThreeChiliPiperUrl'
> {}

interface TableHeadingCellProps {
  label: string;
  buttonLabel?: string;
  link?: string;
  startIcon?: ButtonProps['startIcon'];
  endIcon?: ButtonProps['endIcon'];
  isHighlighted?: boolean;
  chiliPiperUrl?: string;
}

const openChiliPiperPopup = (url: string) => {
  const width = 600;
  const height = 700;
  const left = Math.round(window.screenX + (window.outerWidth - width) / 2);
  const top = Math.round(window.screenY + (window.outerHeight - height) / 2);
  window.open(url, 'chilipiper', `width=${width},height=${height},left=${left},top=${top}`);
};

const TableHeadingCell: FC<TableHeadingCellProps> = ({
  link,
  label,
  buttonLabel,
  startIcon,
  endIcon,
  isHighlighted,
  chiliPiperUrl,
}) => (
  <div className="flex w-full flex-col gap-6">
    <div className="flex w-full items-center justify-center gap-2.5">
      <span className={pricingPlanTitleStyle({ isHighlighted: !!isHighlighted })}>{label}</span>
      {/* Only the highlighted plan renders the badge: hiding it with a CSS class left "Most Popular" in the HTML
          under every plan, and text extractors don't apply CSS. */}
      {isHighlighted && <Badge label="Most Popular" color="teal" />}
    </div>
    <Button
      label={buttonLabel || 'Contact us'}
      hierarchy={isHighlighted ? 'primary' : 'secondary'}
      href={chiliPiperUrl ? undefined : link}
      onClick={chiliPiperUrl ? () => openChiliPiperPopup(chiliPiperUrl) : undefined}
      startIcon={startIcon}
      endIcon={endIcon}
      className="w-full md:w-full lg:w-full"
    />
  </div>
);

const TableHeading: FC<TableHeadingProps> = ({
  tableHeading,
  tierOneTitle,
  tierOneLabel,
  tierOneLink,
  tierOneStartIcon,
  tierOneEndIcon,
  tierOneHighlighted,
  tierOneChiliPiperUrl,
  tierTwoTitle,
  tierTwoLabel,
  tierTwoLink,
  tierTwoStartIcon,
  tierTwoEndIcon,
  tierTwoHighlighted,
  tierTwoChiliPiperUrl,
  tierThreeTitle,
  tierThreeLabel,
  tierThreeLink,
  tierThreeStartIcon,
  tierThreeEndIcon,
  tierThreeHighlighted,
  tierThreeChiliPiperUrl,
}) => (
  <div className="flex items-center lg:gap-[30px]">
    <h2 className="w-full text-display-sm font-bold text-white">{tableHeading}</h2>
    <TableHeadingCell
      label={tierOneTitle}
      buttonLabel={tierOneLabel}
      link={tierOneLink}
      startIcon={tierOneStartIcon}
      endIcon={tierOneEndIcon}
      isHighlighted={tierOneHighlighted}
      chiliPiperUrl={tierOneChiliPiperUrl}
    />
    <TableHeadingCell
      label={tierTwoTitle}
      buttonLabel={tierTwoLabel}
      link={tierTwoLink}
      startIcon={tierTwoStartIcon}
      endIcon={tierTwoEndIcon}
      isHighlighted={tierTwoHighlighted}
      chiliPiperUrl={tierTwoChiliPiperUrl}
    />
    <TableHeadingCell
      label={tierThreeTitle}
      buttonLabel={tierThreeLabel}
      link={tierThreeLink}
      startIcon={tierThreeStartIcon}
      endIcon={tierThreeEndIcon}
      isHighlighted={tierThreeHighlighted}
      chiliPiperUrl={tierThreeChiliPiperUrl}
    />
  </div>
);

export default TableHeading;
